import { useCallback } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import type { Stripe, StripeCardNumberElement } from '@stripe/stripe-js';
import { billing } from '@/services/billing';
import { IS_STRIPE_TEST_MODE } from '@/services/billing/stripe/elements';
import { sentryCaptureException } from '@/utils/sentry';

export type CreditCardFormAddress = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  country: string;
  state: string;
  zipCode: string;
};

export type CreditCardSubmitResult = {
  setupIntent: unknown;
  stripeCustomerToken?: string;
  /**
   * The billing address just confirmed and saved by this submit. Callers (e.g. the Ordway checkout
   * subscribe) should read the contact from here rather than the session account, which only reflects
   * the save after an async re-fetch. Optional so callers/tests that don't need it can omit it.
   */
  address?: CreditCardFormAddress;
};

export type CreditCardSubmitSnackbars = {
  create: (options: {
    id?: string;
    variant: 'critical';
    message: string;
    action?: { label: string; onClick: () => void };
  }) => void;
};

export type CreditCardSubmitDeps = {
  name: string;
  address: CreditCardFormAddress;
  isOrdwayCustomer: boolean;
  invoiceEmail: string;
  stripe: Stripe | null;
  cardNumberElement: StripeCardNumberElement | null;
  /** Persists the billing address on the account (a `PATCH`/`PUT` account). */
  updateAccountAddress: (address: CreditCardFormAddress) => Promise<void>;
  /** Called with the confirmed SetupIntent once the card is processed and the address saved. */
  onSubmit: (result: CreditCardSubmitResult) => Promise<void>;
};

// In test mode the confirmed SetupIntent is stubbed (no Stripe.js). Matches the shape the billing
// card endpoint receives, so page tests can assert the request body.
export const TEST_SETUP_INTENT = { id: 'mock Stripe setup intent' };

/**
 * Returns a submit function for the credit-card form. It owns the error snackbars (via
 * `useSnackbars`, so the caller never passes them) and swallows failures after they've been
 * surfaced, so the void-invoked submit never leaks an unhandled rejection (e.g. the billing-address
 * save rethrow).
 */
export function useCreditCardFormSubmit(): (deps: CreditCardSubmitDeps) => Promise<void> {
  const snackbars = useSnackbars();

  return useCallback(
    async (deps: CreditCardSubmitDeps) => {
      // `run` re-invokes itself for the Stripe-error snackbar's "Try again" action.
      const run = async (): Promise<void> => {
        await submitCreditCardForm(deps, snackbars, () => void run());
      };

      try {
        await run();
      } catch (error) {
        sentryCaptureException(error instanceof Error ? error : new Error('Credit-card submit failed'));
      }
    },
    [snackbars],
  );
}

/**
 * Orchestrates the submission: fetch a Stripe SetupIntent, confirm the card, save the billing
 * address (and the Ordway profile for Ordway accounts), then hand the confirmed SetupIntent to
 * `onSubmit`. Surfaces a specific error snackbar and returns early at each failure point. Exported
 * for unit testing; the form uses {@link useCreditCardFormSubmit}.
 */
export async function submitCreditCardForm(
  deps: CreditCardSubmitDeps,
  snackbars: CreditCardSubmitSnackbars,
  retry: () => void,
): Promise<void> {
  if (IS_STRIPE_TEST_MODE) {
    await saveBillingAddress(deps, undefined, snackbars);
    await deps.onSubmit({ setupIntent: TEST_SETUP_INTENT, stripeCustomerToken: undefined, address: deps.address });
    return;
  }

  let clientSecret: string;
  let stripeCustomerToken: string | undefined;
  try {
    const intent = await billing.getSetupIntent.perform();
    clientSecret = intent.setup_intent_client_secret;
    stripeCustomerToken = intent.stripe_customer_token ?? undefined;
  } catch {
    snackbars.create({
      variant: 'critical',
      id: 'stripe.setupIntentError',
      message: "Our billing partner couldn't be reached. Try again.",
    });
    return;
  }

  if (deps.stripe == null || deps.cardNumberElement == null) {
    // Stripe.js hasn't finished loading; tell the user rather than finishing the flow silently.
    snackbars.create({
      variant: 'critical',
      id: 'stripe.notReady',
      message: 'Stripe is still loading. Try again.',
    });
    return;
  }

  let setupIntent: unknown;
  try {
    const result = await deps.stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: deps.cardNumberElement,
        billing_details: { name: deps.name, address: { postal_code: deps.address.zipCode } },
      },
    });

    if (result == null || result.error != null) {
      snackbars.create({
        variant: 'critical',
        id: 'stripe.handleCardSetupError',
        message: result?.error?.message ?? 'Your card could not be processed.',
      });
      return;
    }
    setupIntent = result.setupIntent;
  } catch (error) {
    sentryCaptureException(error instanceof Error ? error : new Error('Error processing card from stripe'));
    snackbars.create({
      variant: 'critical',
      id: 'stripe.handleCardSetupError',
      message: 'Your card could not be processed.',
      action: { label: 'Try again', onClick: retry },
    });
    return;
  }

  await saveBillingAddress(deps, stripeCustomerToken, snackbars);
  await deps.onSubmit({ setupIntent, stripeCustomerToken, address: deps.address });
}

async function saveBillingAddress(
  deps: CreditCardSubmitDeps,
  stripeCustomerToken: string | undefined,
  snackbars: CreditCardSubmitSnackbars,
): Promise<void> {
  try {
    await deps.updateAccountAddress(deps.address);

    if (deps.isOrdwayCustomer) {
      await billing.updateOrdwayCustomerBillingDetails(
        {
          address1: deps.address.addressLine1,
          address2: deps.address.addressLine2,
          city: deps.address.city,
          state: deps.address.state,
          zip: deps.address.zipCode,
          country: deps.address.country,
          email: deps.invoiceEmail,
        },
        stripeCustomerToken,
      );
    }
  } catch (error) {
    snackbars.create({
      variant: 'critical',
      id: 'billing.updateAddressError',
      message: "Your billing address couldn't be updated. Try again.",
    });
    throw error;
  }
}
